import { Check, Mail, Plus, X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { extractApiError, FormModal } from '@components/branded-modal'
import { AdasBox, AdasButton, AdasTextField, AdasTypography } from '@components/wrapper-components'
import {
  getEmailHistory,
  getQWIByInvoiceId,
  getQWIByQuoteId,
  getQWIByWorkOrderId,
  sendInvoiceEmail,
  sendQuoteEmail,
  sendWorkorderEmail
} from '../../api/api'
import { Logger } from '../../logger'

import parse from 'html-react-parser'
import { convertToMBSize } from 'utils/utils'

// Explicit routing table keyed by emailType. The endpoint is chosen from this
// intent — never inferred from which PDF prop happens to be loaded — so a quote
// send can never silently fall through to the work-order endpoint (CAR-3676).
export const EMAIL_TYPE = { QUOTE: 'quote', WORKORDER: 'workorder', INVOICE: 'invoice' }

const EMAIL_TYPE_CONFIG = {
  [EMAIL_TYPE.QUOTE]: { send: sendQuoteEmail, getQwi: getQWIByQuoteId, stage: 'QUOTE', title: 'Email Quote' },
  [EMAIL_TYPE.WORKORDER]: { send: sendWorkorderEmail, getQwi: getQWIByWorkOrderId, stage: 'WORKORDER', title: 'Email Work Order' },
  [EMAIL_TYPE.INVOICE]: { send: sendInvoiceEmail, getQwi: getQWIByInvoiceId, stage: 'INVOICE', title: 'Email Invoice' }
}

export const AddEmailAddressButton = (props) => {
  const { onClickFunc } = props
  return (
    <button
      type='button'
      onClick={() => onClickFunc(true)}
      className='inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1F6FEB] hover:underline'
    >
      <Plus className='h-4 w-4' />
      Add Email Address
    </button>
  )
}

export const AddEmailInput = (props) => {
  const { isBcc = false, saveEmail, setIsAddBcc, setIsAddSendTo } = props
  const [emailInput, setEmailInput] = useState('')
  const emailInputRef = useRef()
  const handleInputChange = (e) => {
    setEmailInput(e.target.value)
  }
  const handleClose = () => {
    if (isBcc) {
      setIsAddBcc(false)
    } else {
      setIsAddSendTo(false)
    }
  }
  const isValid = emailInput && emailInputRef.current && emailInputRef.current.validity.valid
  return (
    <AdasBox className='mb-2 flex items-center gap-2'>
      <AdasTextField
        onChange={handleInputChange}
        id='email'
        name='email'
        placeholder='email@example.com'
        variant='outlined'
        type='email'
        value={emailInput}
        fullWidth
        size='small'
        InputLabelProps={{ shrink: true }}
        inputRef={emailInputRef}
      />
      <button
        type='button'
        onClick={() => saveEmail({ email: emailInput, checked: true }, isBcc)}
        disabled={!isValid}
        className='rounded-lg bg-[#0A1628] px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-40'
      >
        Save
      </button>
      <button
        type='button'
        onClick={handleClose}
        aria-label='Cancel add email'
        className='inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#D1D5D8] bg-white text-[#475569] transition-colors hover:bg-[#F8FAFC]'
      >
        <X className='h-4 w-4' />
      </button>
    </AdasBox>
  )
}

export const EmailItem = (props) => {
  const { isChecked, item, isBcc, updateFunc, isAddedEmail, handleCheckbox, sentAt } = props
  const formattedDate = sentAt ? new Date(sentAt).toLocaleDateString() : ''
  const formattedTime = sentAt ? new Date(sentAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''
  return (
    <label
      key={item.email}
      className={`flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
        isChecked ? 'bg-[#E8EFFF]' : 'bg-white hover:bg-[#F8FAFC]'
      }`}
    >
      <span
        className={`mt-0.5 inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border transition-colors ${
          isChecked ? 'border-[#0A1628] bg-[#0A1628]' : 'border-[#D1D5D8] bg-white'
        }`}
      >
        {isChecked && <Check className='h-3 w-3 text-white' strokeWidth={3} />}
      </span>
      <input
        type='checkbox'
        className='hidden'
        checked={isChecked}
        onChange={(e) => handleCheckbox(e, item, isBcc, isAddedEmail, updateFunc)}
      />
      <AdasBox className='min-w-0 flex-1'>
        {item.name && <AdasTypography className='truncate text-[14px] font-semibold text-[#0A1628]'>{item.name}</AdasTypography>}
        <AdasTypography className='truncate text-[13px] text-[#475569]'>{item.email}</AdasTypography>
        {sentAt && (
          <AdasTypography className='mt-0.5 text-[11px] italic text-[#6B7280]'>
            Last sent: {formattedDate} @ {formattedTime}
          </AdasTypography>
        )}
      </AdasBox>
    </label>
  )
}

const PdfInfo = (data) =>
  data && (
    <AdasBox className='flex items-center justify-between gap-3 rounded-lg border border-[#EBEDF0] bg-white px-4 py-3'>
      <a
        download={data.name}
        href={URL.createObjectURL(data)}
        className='truncate text-[14px] font-semibold text-[#1F6FEB] hover:underline'
      >
        {data.name}
      </a>
      <span className='shrink-0 text-[12px] text-[#6B7280]'>{convertToMBSize(data.size)}</span>
    </AdasBox>
  )

const SectionLabel = ({ children }) => (
  <AdasTypography className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>{children}</AdasTypography>
)
SectionLabel.propTypes = { children: PropTypes.node }

export const EmailForm = (props) => {
  const { isOpen, emailData, onClose, contacts, emailType, quotePdf, workOrderPdf, invoicePdf, invoicePdfs, emailVariants } = props
  const config = EMAIL_TYPE_CONFIG[emailType] || null
  // The attachment(s) that belong to this email type. SEND stays disabled until they
  // have loaded, so we never send before the correct PDF is ready. An invoice can carry
  // more than one document — the retail invoice, the cost invoice, or both — so callers
  // may pass `invoicePdfs`; every other type carries exactly one.
  const activePdfs = useMemo(() => {
    const loaded =
      emailType === EMAIL_TYPE.INVOICE
        ? (invoicePdfs ?? invoicePdf)
        : emailType === EMAIL_TYPE.QUOTE
          ? quotePdf
          : emailType === EMAIL_TYPE.WORKORDER
            ? workOrderPdf
            : undefined
    return [].concat(loaded ?? []).filter(Boolean)
  }, [emailType, invoicePdf, invoicePdfs, quotePdf, workOrderPdf])
  // How many documents this send is meant to carry — known from the chosen variants even
  // while the PDFs are still loading, so the copy below reads right from the first frame.
  const expectedAttachmentCount = Math.max(emailVariants?.length || 0, activePdfs.length, 1)
  const [emailList, setEmailList] = useState({
    sendTos: [],
    bccs: []
  })
  const [qwiEmailHistory, setQwiEmailHistory] = useState([])
  const [isAddSendTo, setIsAddSendTo] = useState(false)
  const [isAddBcc, setIsAddBcc] = useState(false)
  const [addEmailList, setAddEmailList] = useState({
    sendTos: [],
    bccs: []
  })
  const formTitle = config?.title || 'Email'
  const [apiError, setApiError] = useState(null)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const currentUser = useUserStore((store) => store.currentUser)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const managementBccList = useMemo(() => {
    const isPrivileged = currentUser?.is_admin || currentUser?.is_owner || currentUser?.is_manager
    const allUsers = Array.from(
      new Set([
        ...(centerDetail?.owners ? centerDetail.owners : []),
        ...(centerDetail?.managers ? centerDetail.managers : []),
        ...(contacts && contacts.bccs && contacts.bccs.length > 0 ? contacts.bccs : []),
        ...(currentUser && !isPrivileged ? [currentUser] : [])
      ])
    )
    return Array.from(new Set(allUsers.filter((user) => user?.is_active === true)))
  }, [centerDetail, contacts, currentUser])

  useEffect(() => {
    const fetchData = async () => {
      if (!config) return
      try {
        const qwiMapping = await config.getQwi(emailData.id)
        const params = { qwi_id: qwiMapping?.data?.id, qwi_stage: config.stage }

        if (params.qwi_id && params.qwi_stage) {
          const response = await getEmailHistory(params)
          if (response?.data && response?.data.length > 0) {
            setQwiEmailHistory(response?.data)
          }
        }
      } catch (error) {
        Logger.error({ message: error, payload: { emailId: emailData.id, file: 'EmailForm', path: 'useEffect/fetchData' } })
      }
    }

    if (emailData.id) fetchData()
  }, [emailType, emailData.id])

  const saveEmail = useCallback(
    (props, isBcc) => {
      if (isBcc) {
        setAddEmailList((prev) => {
          const bccList = [...prev.bccs]
          bccList.push(props)
          const updated = {
            ...prev,
            bccs: bccList
          }
          return updated
        })
        setIsAddBcc(false)
      } else {
        setAddEmailList((prev) => {
          const sendToList = [...prev.sendTos]
          sendToList.push(props)
          const updated = {
            ...prev,
            sendTos: sendToList
          }
          return updated
        })
        setIsAddSendTo(false)
      }
    },
    [setAddEmailList, setIsAddBcc, setIsAddSendTo]
  )

  const reset = useCallback(() => {
    setEmailList({ sendTos: [], bccs: [] })
    setAddEmailList({ sendTos: [], bccs: [] })
    setIsAddBcc(false)
    setIsAddSendTo(false)
    // Clear any prior send failure so a stale error doesn't persist when the
    // modal is closed and reopened (the form stays mounted, toggled via isOpen).
    setApiError(null)
    onClose()
  }, [onClose])

  const handleCheckbox = useCallback((e, props, isBcc, isAddedEmail, updateFunc) => {
    if (isBcc) {
      if (isAddedEmail) {
        updateFunc((prev) => {
          const bccList = [...prev.bccs]
          const index = bccList.indexOf(props)
          bccList[index].checked = e.target.checked
          const updated = {
            ...prev,
            bccs: bccList
          }
          return updated
        })
      } else {
        updateFunc((prev) => {
          const bccList = [...prev.bccs]
          if (e.target.checked) {
            bccList.push(props)
          } else {
            const index = bccList.indexOf(props)
            bccList.splice(index, 1)
          }
          const updated = {
            ...prev,
            bccs: bccList
          }
          return updated
        })
      }
    } else {
      if (isAddedEmail) {
        updateFunc((prev) => {
          const sendToList = [...prev.sendTos]
          const index = sendToList.indexOf(props)
          sendToList[index].checked = e.target.checked
          const updated = {
            ...prev,
            sendTos: sendToList
          }
          return updated
        })
      } else {
        updateFunc((prev) => {
          const sendToList = [...prev.sendTos]
          if (e.target.checked) {
            sendToList.push(props)
          } else {
            const index = sendToList.indexOf(props)
            sendToList.splice(index, 1)
          }
          const updated = {
            ...prev,
            sendTos: sendToList
          }
          return updated
        })
      }
    }
  }, [])

  const sendEmailHandler = useCallback(async () => {
    if (!config) {
      Logger.error({
        message: 'EmailForm: missing or invalid emailType',
        payload: { emailType, emailId: emailData.id, path: 'sendEmailHandler' }
      })
      setApiError('Unable to send email: email type is not set. Please reopen the email dialog and try again.')
      return
    }

    // Defense in depth: never send while a loaded attachment belongs to a
    // different record than emailData.id (stale/transitional state). The SEND
    // button is already disabled in this case; this guards the handler too.
    const mismatched = activePdfs.find((pdf) => pdf?.id != null && String(pdf.id) !== String(emailData.id))
    if (mismatched) {
      Logger.error({
        message: 'EmailForm: attachment id does not match emailData.id — send blocked',
        payload: { emailType, attachmentId: mismatched.id, emailId: emailData.id, path: 'sendEmailHandler' }
      })
      setApiError('Unable to send email: the attachment does not match this record. Please reopen the email dialog and try again.')
      return
    }

    const data = {
      sendTos: [...emailList.sendTos, ...addEmailList.sendTos.filter((item) => item.checked)],
      bccs: [...emailList.bccs, ...addEmailList.bccs.filter((item) => item.checked)],
      subject: contacts.subject,
      message: contacts.message,
      info: contacts.info,
      // Only invoices offer a document choice (retail / cost / both); the key is left off
      // entirely for quotes and work orders so their payload is unchanged.
      ...(emailType === EMAIL_TYPE.INVOICE && emailVariants?.length ? { variants: emailVariants } : {})
    }

    const params = {
      data,
      id: emailData.id
    }

    setLoading(true)
    setApiError(null)
    try {
      const sendEmailResponse = await config.send(params)

      if (sendEmailResponse?.status === 200) {
        reset()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { emailId: emailData.id, path: 'sendEmailHandler' } })
      setApiError(extractApiError(error) || 'Failed to send email. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [emailList, addEmailList, contacts, config, emailType, emailData.id, activePdfs, emailVariants, reset])
  const selectedSendTos = [...emailList.sendTos, ...addEmailList.sendTos.filter((item) => item.checked)]

  const selectedBcs = [...emailList.bccs, ...addEmailList.bccs.filter((item) => item.checked)]

  const isSendToDisabled = selectedSendTos.length === 0 && selectedBcs.length === 0
  // Block SEND until the attachment(s) for THIS email type have loaded AND belong to
  // the entity we're about to send (emailData.id). Callers stamp each loaded PDF
  // with `.id`; requiring a match closes both the type fall-through and the window
  // where a previous entity's PDF still lingers in state before the new fetch
  // resolves (CAR-3676). When a PDF carries no id we fall back to a presence check
  // so this never hard-breaks a caller that doesn't stamp one.
  const isAttachmentReady = activePdfs.length > 0 && activePdfs.every((pdf) => pdf.id == null || String(pdf.id) === String(emailData.id))
  const isSendDisabled = isSendToDisabled || !isAttachmentReady

  const totalRecipients =
    (contacts?.sendTos?.length || 0) + addEmailList.sendTos.length + managementBccList.length + addEmailList.bccs.length
  const selectedCount = selectedSendTos.length + selectedBcs.length

  const footer = (
    <div className='flex items-center justify-end gap-3'>
      <AdasButton variant='outlined' onClick={reset} className='rounded-lg px-5'>
        CANCEL
      </AdasButton>
      <AdasButton
        variant='contained'
        onClick={sendEmailHandler}
        disabled={isSendDisabled}
        className='rounded-lg bg-[#0A1628] px-5 text-white hover:bg-[#1F2D3D]'
      >
        SEND
      </AdasButton>
    </div>
  )

  return (
    <FormModal
      open={isOpen}
      onClose={reset}
      title={formTitle}
      icon={Mail}
      eyebrow={`${selectedCount} of ${totalRecipients} selected`}
      width='max-w-[560px]'
      footer={footer}
      errorMessage={apiError}
      onDismissError={() => setApiError(null)}
    >
      <AdasBox>
        {/* Send To */}
        <AdasBox className='mb-5'>
          <SectionLabel>Send To</SectionLabel>
          <AdasBox className='flex flex-col gap-1.5'>
            {contacts?.sendTos?.length > 0 &&
              contacts.sendTos.map((item) => {
                const emailHistory = qwiEmailHistory?.find((history) => history.sent_email === item.email)
                const sentAt = emailHistory ? emailHistory.sent_at : null
                return (
                  <EmailItem
                    isChecked={!!emailList.sendTos.find((contact) => contact.email === item.email)}
                    item={item}
                    updateFunc={setEmailList}
                    isBcc={false}
                    handleCheckbox={handleCheckbox}
                    key={item.email}
                    sentAt={sentAt}
                  />
                )
              })}
            {addEmailList.sendTos.map((item) => {
              const emailHistory = qwiEmailHistory?.find((history) => history.sent_email === item.email)
              const sentAt = emailHistory ? emailHistory.sent_at : null
              return (
                <EmailItem
                  isChecked={item.checked}
                  item={item}
                  updateFunc={setAddEmailList}
                  isBcc={false}
                  isAddedEmail={true}
                  handleCheckbox={handleCheckbox}
                  key={item.email}
                  sentAt={sentAt}
                />
              )
            })}
          </AdasBox>
          <AdasBox className='mt-2'>
            {isAddSendTo ? (
              <AddEmailInput saveEmail={saveEmail} isBcc={false} setIsAddBcc={setIsAddBcc} setIsAddSendTo={setIsAddSendTo} />
            ) : (
              <AddEmailAddressButton onClickFunc={() => setIsAddSendTo(true)} />
            )}
          </AdasBox>
        </AdasBox>

        {/* Bcc */}
        <AdasBox className='mb-5'>
          <SectionLabel>BCC (Blind Carbon Copy)</SectionLabel>
          <AdasBox className='flex flex-col gap-1.5'>
            {managementBccList?.map((item, index) => {
              const emailHistory = qwiEmailHistory?.find((history) => history.sent_email === item.email)
              const sentAt = emailHistory ? emailHistory.sent_at : null
              return (
                <EmailItem
                  isChecked={!!emailList.bccs.find((contact) => contact.email === item.email)}
                  item={item}
                  updateFunc={setEmailList}
                  isBcc={true}
                  handleCheckbox={handleCheckbox}
                  key={item.email + index}
                  sentAt={sentAt}
                />
              )
            })}
            {addEmailList.bccs.map((item) => {
              const emailHistory = qwiEmailHistory?.find((history) => history.sent_email === item.email)
              const sentAt = emailHistory ? emailHistory.sent_at : null
              return (
                <EmailItem
                  isChecked={item.checked}
                  item={item}
                  updateFunc={setAddEmailList}
                  isBcc={true}
                  isAddedEmail={true}
                  handleCheckbox={handleCheckbox}
                  key={item.email}
                  sentAt={sentAt}
                />
              )
            })}
          </AdasBox>
          <AdasBox className='mt-2'>
            {isAddBcc ? (
              <AddEmailInput saveEmail={saveEmail} isBcc={true} setIsAddBcc={setIsAddBcc} setIsAddSendTo={setIsAddSendTo} />
            ) : (
              <AddEmailAddressButton onClickFunc={() => setIsAddBcc(true)} />
            )}
          </AdasBox>
        </AdasBox>

        {/* Email Preview */}
        <AdasBox className='mb-5'>
          <SectionLabel>Email Preview</SectionLabel>
          <AdasBox className='space-y-3 rounded-lg border border-[#EBEDF0] bg-white px-4 py-3'>
            <AdasBox>
              <AdasTypography className='mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Subject</AdasTypography>
              <AdasTypography className='text-[14px] text-[#0A1628]'>{contacts.subject}</AdasTypography>
            </AdasBox>
            <AdasBox>
              <AdasTypography className='mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Message</AdasTypography>
              <AdasTypography className='text-[13px] text-[#475569]'>{contacts.message && parse(contacts.message)}</AdasTypography>
            </AdasBox>
            <AdasBox>
              <AdasTypography className='mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
                Calibration Center Info
              </AdasTypography>
              <AdasTypography className='text-[13px] text-[#475569]'>{contacts.info && parse(contacts.info)}</AdasTypography>
            </AdasBox>
          </AdasBox>
        </AdasBox>

        {/* Attachment */}
        <AdasBox>
          <SectionLabel>{expectedAttachmentCount > 1 ? 'Attachments' : 'Attachment'}</SectionLabel>
          {isAttachmentReady ? (
            <AdasBox className='flex flex-col gap-2'>
              {activePdfs.map((pdf) => (
                <AdasBox key={pdf.name}>{PdfInfo(pdf)}</AdasBox>
              ))}
            </AdasBox>
          ) : (
            <AdasBox className='rounded-lg border border-[#EBEDF0] bg-white px-4 py-3 text-[13px] italic text-[#6B7280]'>
              {expectedAttachmentCount > 1
                ? 'Preparing attachments… SEND will be enabled once they are ready.'
                : 'Preparing attachment… SEND will be enabled once it is ready.'}
            </AdasBox>
          )}
        </AdasBox>
      </AdasBox>
    </FormModal>
  )
}

EmailItem.propTypes = {
  isChecked: PropTypes.bool,
  item: PropTypes.object,
  isBcc: PropTypes.bool,
  updateFunc: PropTypes.func,
  isAddedEmail: PropTypes.bool,
  handleCheckbox: PropTypes.func,
  sentAt: PropTypes.string
}

AddEmailInput.propTypes = {
  isBcc: PropTypes.bool,
  saveEmail: PropTypes.func,
  setIsAddBcc: PropTypes.func,
  setIsAddSendTo: PropTypes.func
}

EmailForm.propTypes = {
  isOpen: PropTypes.bool,
  emailData: PropTypes.object,
  onClose: PropTypes.func,
  contacts: PropTypes.object,
  emailType: PropTypes.oneOf([EMAIL_TYPE.QUOTE, EMAIL_TYPE.WORKORDER, EMAIL_TYPE.INVOICE]).isRequired,
  quotePdf: PropTypes.object,
  workOrderPdf: PropTypes.object,
  invoicePdf: PropTypes.object,
  // One entry per invoice document being sent — retail, cost, or both. Takes precedence
  // over `invoicePdf` when supplied.
  invoicePdfs: PropTypes.arrayOf(PropTypes.object),
  emailVariants: PropTypes.arrayOf(PropTypes.string)
}

AddEmailAddressButton.propTypes = {
  onClickFunc: PropTypes.func.isRequired
}
